{
  "blocks": [
    {
      "id": "moon-article-first-appointment",
      "kind": "article",
      "tags": ["moon", "moon-guides"],
      "title": "What happens at your first fertility appointment",
      "subtitle": "What to expect, what to bring, and what questions to ask.",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "Start here",
        "quickSummary": [
          {
            "title": "It's an information-gathering visit.",
            "description": "No procedures on day one: your doctor is focused on understanding your history, goals, and building a preliminary plan."
          },
          {
            "title": "Bring your medical records.",
            "description": "Insurance info, any prior test results, and a list of questions. The more prepared you are, the more productive the visit."
          },
          {
            "title": "You'll leave with a plan.",
            "description": "Expect a preliminary roadmap and likely some blood work orders to get the diagnostic process started."
          }
        ],
        "sections": [
          {
            "title": "What to expect",
            "content": "Your first fertility appointment is typically 45–60 minutes. You'll meet with a reproductive endocrinologist (RE), a specialist in fertility medicine. The visit is conversational: your doctor wants to understand your medical history, how long you've been trying (if applicable), and what your goals are. There are no procedures on the first day.",
            "bulletPoints": [
              "Plan for 45–60 minutes including intake paperwork",
              "You'll meet with an RE, not a general OB/GYN",
              "The visit is a conversation, no needles or procedures",
              "Some clinics offer virtual first consultations"
            ]
          },
          {
            "title": "What to bring",
            "content": "Come prepared to make the most of your time. If you have a partner, it's helpful (but not required) for them to attend. Many clinics send intake forms in advance. Fill them out thoroughly before you arrive.",
            "bulletPoints": [
              "Insurance card and photo ID",
              "Any prior blood work, ultrasound results, or surgical records",
              "A list of current medications and supplements",
              "Your partner, if possible (some questions may involve both of you)",
              "A written list of your questions (you will forget them otherwise)"
            ]
          },
          {
            "title": "The medical history review",
            "content": "Your RE will walk through your menstrual cycle history, prior pregnancies (if any), surgical history, family medical history, and lifestyle factors. Be honest and thorough: details about cycle length, irregularity, painful periods, or previous diagnoses like PMOS (ex PCOS) or endometriosis help your doctor plan the right workup."
          },
          {
            "title": "Initial testing ordered",
            "content": "Based on your history, your doctor will order a set of baseline tests. These are typically scheduled for day 2–3 of your next period. The goal is to assess ovarian reserve, hormone levels, and reproductive anatomy.",
            "bulletPoints": [
              "Blood work: AMH, FSH, estradiol, TSH, prolactin",
              "Baseline transvaginal ultrasound with antral follicle count (AFC)",
              "Semen analysis (SA) for male partners, often done at a separate visit",
              "Additional tests like HSG or saline sonogram may be recommended"
            ]
          },
          {
            "title": "Questions to ask your RE",
            "content": "Your first visit is your chance to evaluate your doctor as much as they're evaluating you. Don't be shy: a good RE welcomes questions and takes time to answer them.",
            "bulletPoints": [
              "Based on my history, what do you think is going on?",
              "What tests do you recommend and why?",
              "What treatment options are you considering for me?",
              "What are the success rates for someone in my situation?",
              "How does your clinic handle communication and results?",
              "What's the timeline from here to a treatment plan?"
            ]
          },
          {
            "title": "What happens after",
            "content": "After your initial testing is complete (usually within one cycle), you'll have a follow-up appointment to review results and discuss a concrete plan. This is when your doctor will recommend a specific path, whether that's monitoring, medication, IUI, IVF, or further diagnostics. The first appointment sets everything in motion."
          }
        ]
      }
    },
    {
      "id": "moon-article-embryo-grading",
      "kind": "article",
      "tags": ["moon", "moon-guides"],
      "title": "Decoding your embryo grades",
      "subtitle": "How to read 4AA, 3BB, and the other grades your clinic will give you.",
      "readTimeMinutes": 8,
      "body": {
        "introduction": "Start here",
        "quickSummary": [
          {
            "title": "Grading is subjective.",
            "description": "It describes appearance, not destiny. Embryologists assess structure under a microscope, but it's not an exact science."
          },
          {
            "title": "A 'perfect' grade doesn't guarantee pregnancy.",
            "description": "And a 'lower' grade doesn't mean failure. Many healthy babies come from embryos that didn't look textbook-perfect."
          },
          {
            "title": "PGT-A and grading measure different things.",
            "description": "PGT-A testing tells you about chromosomes; grading tells you about structure. Both are useful, but they answer different questions."
          }
        ],
        "sections": [
          {
            "title": "Day 3 grading",
            "content": "On day 3 after fertilization, embryos are at the cleavage stage, typically 6–8 cells. Embryologists evaluate two main factors: cell number and fragmentation. An ideal day 3 embryo has 7–8 evenly sized cells with minimal fragmentation (cellular debris between the cells).",
            "bulletPoints": [
              "Cell number: 6–8 cells is ideal on day 3",
              "Fragmentation: graded as a percentage (under 10% is excellent)",
              "Symmetry: evenly sized cells are preferred",
              "Most clinics now culture to day 5 rather than transferring on day 3"
            ]
          },
          {
            "title": "Day 5/6 blastocyst grading",
            "content": "By day 5 or 6, a healthy embryo has become a blastocyst, a fluid-filled structure with two distinct cell groups. Blastocyst grading uses a three-part system: a number for expansion (1–6), a letter for the inner cell mass or ICM (A–C), and a letter for the trophectoderm or TE (A–C).",
            "bulletPoints": [
              "Expansion number (1–6): how expanded the blastocyst is, with 3+ being fully expanded",
              "Inner cell mass (A–C): the cluster of cells that becomes the baby",
              "Trophectoderm (A–C): the outer ring of cells that becomes the placenta",
              "A = many tightly packed cells, B = moderate, C = few or loosely grouped"
            ]
          },
          {
            "title": "What '4AA' actually means",
            "content": "A grade of 4AA means the blastocyst is fully expanded (4), has a tightly packed inner cell mass (A), and a well-organized trophectoderm (A). It's considered a top-quality embryo, but it's not the only grade that leads to healthy pregnancies. Embryos graded 3BB, 4AB, or even 4BC regularly result in live births."
          },
          {
            "title": "Why grades are NOT the same across clinics",
            "content": "There is no universal grading standard. One clinic's 4AA might be another clinic's 3AB. Different embryologists may grade the same embryo differently, and some clinics use entirely different scoring systems. This is why comparing grades across clinics is unreliable.",
            "bulletPoints": [
              "No universal grading standard exists",
              "Some clinics use numerical systems instead of letters",
              "The same embryo can receive different grades from different embryologists",
              "Ask your clinic to explain their specific grading scale"
            ]
          },
          {
            "title": "Grading vs. PGT-A results",
            "content": "Grading and PGT-A answer fundamentally different questions. Grading assesses the physical appearance and structure of the embryo. PGT-A tests whether the embryo has the correct number of chromosomes. A beautifully graded 5AA embryo can be aneuploid (chromosomally abnormal), and a modest-looking 3BB can be euploid (chromosomally normal)."
          },
          {
            "title": "What grades matter for success rates",
            "content": "Higher-graded embryos do have statistically better implantation rates, but the correlation isn't absolute. The most important factor for success is whether the embryo is chromosomally normal, which you can only know through PGT-A. If you have PGT-A results, those matter more than the morphology grade. If you don't, grade is the best proxy your clinic has for selecting which embryo to transfer first."
          }
        ]
      }
    },
    {
      "id": "moon-article-ivf-protocols",
      "kind": "article",
      "tags": ["moon", "moon-guides"],
      "title": "IVF protocols explained",
      "subtitle": "Antagonist, long lupron, mini-IVF: which protocol and why.",
      "readTimeMinutes": 9,
      "body": {
        "introduction": "Start here",
        "quickSummary": [
          {
            "title": "Your protocol is personalized.",
            "description": "It's chosen based on your ovarian reserve, age, and prior response to stimulation, not one-size-fits-all."
          },
          {
            "title": "Antagonist protocol is most common today.",
            "description": "It's shorter, requires fewer injections, and carries a lower risk of ovarian hyperstimulation syndrome (OHSS)."
          },
          {
            "title": "There's no single 'best' protocol.",
            "description": "The right one depends on your specific biology. If a cycle doesn't go as hoped, your doctor may adjust the protocol for the next round."
          }
        ],
        "sections": [
          {
            "title": "What a protocol means",
            "content": "An IVF protocol is the specific combination of medications, dosages, and timing your doctor prescribes for ovarian stimulation. The goal is to safely grow multiple follicles (each containing an egg) while preventing premature ovulation. Different protocols use different strategies to achieve this balance."
          },
          {
            "title": "Antagonist protocol",
            "content": "The antagonist protocol is the most widely used approach today. You start gonadotropin injections (FSH medications like Gonal-F or Follistim) on cycle day 2–3, and add a GnRH antagonist (Cetrotide or Ganirelix) around day 5–6 to prevent premature ovulation. The entire stimulation phase lasts 8–12 days.",
            "bulletPoints": [
              "Most common protocol in modern IVF",
              "Shorter stimulation phase (8–12 days)",
              "Lower risk of ovarian hyperstimulation syndrome (OHSS)",
              "Allows use of Lupron trigger instead of hCG, further reducing OHSS risk",
              "Best for: most patients, especially those with normal to high ovarian reserve"
            ]
          },
          {
            "title": "Long agonist (Lupron) protocol",
            "content": "Also called the long Lupron or down-regulation protocol. You start Lupron injections in the luteal phase of the cycle before your IVF cycle (about a week before your period). Lupron initially stimulates then suppresses your pituitary gland, giving the doctor more control over stimulation timing. Gonadotropins are added once suppression is confirmed.",
            "bulletPoints": [
              "Takes longer: Lupron starts ~1 week before your period",
              "More injections total, but very controlled stimulation",
              "May produce more synchronized follicle growth",
              "Higher OHSS risk compared to antagonist protocol",
              "Best for: patients with endometriosis or those who need very controlled stimulation"
            ]
          },
          {
            "title": "Mini/minimal stimulation IVF",
            "content": "Mini-IVF uses lower doses of medications, often oral medications like Clomid or letrozole combined with low-dose gonadotropins. The goal is to retrieve fewer but potentially higher-quality eggs. It's gentler on the body and significantly less expensive per cycle, but may require more cycles to achieve the same number of eggs.",
            "bulletPoints": [
              "Lower medication doses, often including oral medications",
              "Fewer eggs retrieved per cycle (typically 3–7)",
              "Lower cost per cycle, but may need multiple cycles",
              "Reduced risk of OHSS",
              "Best for: patients who want a gentler approach, those with low ovarian reserve, or those for whom cost is a major factor"
            ]
          },
          {
            "title": "Microdose Lupron flare",
            "content": "This protocol uses very small doses of Lupron at the start of stimulation to create a brief 'flare' of FSH and LH from the pituitary, boosting the body's own hormone production before adding gonadotropins. It's specifically designed for patients who have responded poorly to other protocols.",
            "bulletPoints": [
              "Uses the body's own hormone surge to boost stimulation",
              "Designed for poor responders or diminished ovarian reserve",
              "Starts on cycle day 2 with microdose Lupron",
              "Gonadotropins added on cycle day 3–4"
            ]
          },
          {
            "title": "Natural cycle IVF",
            "content": "Natural cycle IVF involves no stimulation medications at all, or very minimal support. Your doctor monitors your natural cycle and retrieves the single egg your body produces. Success rates per cycle are lower, but there are no stimulation side effects and the cost per cycle is significantly reduced. Some patients prefer this approach for personal or medical reasons."
          },
          {
            "title": "How doctors choose your protocol",
            "content": "Your RE considers several factors when selecting a protocol: your age, AMH level, antral follicle count, BMI, prior IVF history (if any), and any conditions like PMOS (ex PCOS) or endometriosis. If you're doing your first cycle, the choice is based on prediction. If you've done a prior cycle, your response to that protocol heavily informs the next one."
          },
          {
            "title": "Switching protocols between cycles",
            "content": "It's common to switch protocols between cycles. If your first cycle produced fewer eggs than expected, your doctor might try a different protocol: higher doses, a different suppression strategy, or adding adjunct medications like growth hormone or Omnitrope. Each cycle provides data that helps refine the approach. A different protocol doesn't mean the first one failed. It means your doctor is personalizing your care."
          }
        ]
      }
    },
    {
      "id": "moon-article-egg-freezing-timeline",
      "kind": "article",
      "tags": ["moon", "moon-guides"],
      "title": "The egg freezing timeline",
      "subtitle": "A week-by-week look at what to expect during egg freezing.",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "Start here",
        "quickSummary": [
          {
            "title": "Active process takes about 2 weeks.",
            "description": "From your first injection to retrieval day, the stimulation phase lasts 10–14 days."
          },
          {
            "title": "Testing and consultation come first.",
            "description": "Before starting injections, you'll need 2–4 weeks of initial testing, blood work, and a consultation with your RE."
          },
          {
            "title": "Recovery is quick.",
            "description": "Most people return to normal activities within 2–3 days after the retrieval procedure."
          }
        ],
        "sections": [
          {
            "title": "Weeks before: consultation and testing",
            "content": "The process starts well before any injections. Your first step is an initial consultation with a reproductive endocrinologist, followed by baseline testing: blood work (AMH, FSH, estradiol, TSH) and a transvaginal ultrasound to count your antral follicles. Some clinics can complete all of this in a single visit; others spread it over 1–2 appointments.",
            "bulletPoints": [
              "Initial consultation with an RE (in-person or virtual)",
              "Blood work: AMH, FSH, estradiol, TSH, infectious disease screening",
              "Baseline transvaginal ultrasound with antral follicle count",
              "Review results and finalize your stimulation protocol",
              "Order medications (allow 3–5 days for pharmacy delivery)"
            ]
          },
          {
            "title": "Week 1 of stimulation: starting injections",
            "content": "You'll start injections on day 2 or 3 of your period. The first few days typically involve one injection per evening, a gonadotropin like Gonal-F or Follistim. Around day 4–5, your doctor may add a second injection (a GnRH antagonist like Ganirelix) to prevent premature ovulation. You'll have your first monitoring visit around day 4–5.",
            "bulletPoints": [
              "Day 2–3: begin gonadotropin injections",
              "Day 4–5: first monitoring visit (blood work + ultrasound)",
              "Day 5–6: second medication (antagonist) may be added",
              "Injections are subcutaneous: small needles, self-administered at home"
            ]
          },
          {
            "title": "Week 2 of stimulation: monitoring and trigger",
            "content": "Monitoring visits increase to every 1–2 days during week 2. Your doctor tracks follicle growth via ultrasound and adjusts medication doses based on your response. When the lead follicles reach 18–20mm, your doctor will schedule the trigger shot, a precisely timed injection that causes final egg maturation exactly 36 hours before retrieval.",
            "bulletPoints": [
              "Monitoring visits every 1–2 days",
              "Medication doses may be adjusted up or down",
              "Trigger shot timing is exact, often late evening for a morning retrieval",
              "You'll stop all other medications after the trigger"
            ]
          },
          {
            "title": "Retrieval day",
            "content": "Egg retrieval is a short outpatient procedure performed under light sedation: you'll be asleep but won't need general anesthesia. Using ultrasound guidance, the doctor inserts a thin needle through the vaginal wall to aspirate fluid from each follicle. The procedure takes 15–25 minutes. You'll rest in recovery for about an hour before going home. You'll need someone to drive you.",
            "bulletPoints": [
              "Arrive at the clinic 30–60 minutes before your scheduled time",
              "The procedure itself takes 15–25 minutes",
              "Light sedation: you won't feel anything during the procedure",
              "You'll receive your egg count before you leave the clinic",
              "Arrange for someone to drive you home"
            ]
          },
          {
            "title": "Recovery: days 1–3",
            "content": "Most people feel bloated, crampy, and tired on retrieval day and the following day. Over-the-counter pain relief (Tylenol, not ibuprofen) is usually sufficient. By day 2–3, most people feel significantly better and return to normal activities. Avoid strenuous exercise for about a week.",
            "bulletPoints": [
              "Day 0: rest at home, expect bloating and mild cramping",
              "Day 1: most people feel noticeably better",
              "Day 2–3: most return to work and normal activities",
              "Call your doctor if you experience severe pain, vomiting, difficulty breathing, or very low urine output (these can be signs of OHSS)"
            ]
          },
          {
            "title": "After: storage and next steps",
            "content": "Your eggs are vitrified (flash-frozen) and stored in liquid nitrogen tanks at your clinic or a specialized storage facility. Annual storage fees typically range from $500–$1,000. There's no evidence that storage duration affects egg quality: eggs frozen for 10 years perform the same as those frozen for 1 year. When you're ready to use them, the eggs are thawed, fertilized with sperm via ICSI, and the resulting embryos are transferred."
          }
        ]
      }
    }
  ],
  "relationships": [],
  "localeVariants": []
}
